/**
 * Enhanced language detection utility that respects browser language preferences
 * and provides priority-based language selection
 */

export interface LanguagePreference {
  language: string;
  quality: number; // 1.0 = highest quality, 0.0 = lowest
}

/**
 * Parse Accept-Language header format and return sorted language preferences
 * @param acceptLanguage - The Accept-Language header value
 * @returns Array of language preferences sorted by quality
 */
export function parseAcceptLanguage(acceptLanguage: string): LanguagePreference[] {
  if (!acceptLanguage) return [];

  const preferences: LanguagePreference[] = [];

  // Split by comma and parse each language tag
  const languageTags = acceptLanguage.split(',').map(tag => tag.trim());

  for (const tag of languageTags) {
    const [language, qualityValue] = tag.split(';q=');
    const quality = qualityValue ? parseFloat(qualityValue) : 1.0;

    // Normalize language codes (e.g., 'es-MX' -> 'es-MX', 'es' -> 'es')
    const normalizedLanguage = language.toLowerCase().trim();

    preferences.push({
      language: normalizedLanguage,
      quality
    });
  }

  // Sort by quality (highest first)
  return preferences.sort((a, b) => b.quality - a.quality);
}

/**
 * Get browser language preferences from navigator.languages or navigator.language
 * @returns Array of language preferences
 */
export function getBrowserLanguagePreferences(): LanguagePreference[] {
  // Use navigator.languages if available (modern browsers)
  if (navigator.languages && navigator.languages.length > 0) {
    return navigator.languages.map((lang, index) => ({
      language: lang.toLowerCase(),
      quality: Math.max(1.0 - (index * 0.1), 0.1) // Decreasing quality for each fallback
    }));
  }

  // Fallback to navigator.language
  if (navigator.language) {
    return [{
      language: navigator.language.toLowerCase(),
      quality: 1.0
    }];
  }

  return [];
}

/**
 * Find the best matching language from available languages based on browser preferences
 * @param availableLanguages - Array of available language codes
 * @param browserPreferences - Browser language preferences (optional, will be detected if not provided)
 * @returns Best matching language or fallback
 */
export function findBestLanguageMatch(
  availableLanguages: string[],
  browserPreferences?: LanguagePreference[]
): string {
  const preferences = browserPreferences || getBrowserLanguagePreferences();

  if (preferences.length === 0) {
    return availableLanguages[0] || 'en'; // Default fallback
  }

  // Create a normalized set of available languages
  const normalizedAvailable = availableLanguages.map(lang => lang.toLowerCase());

  // First pass: exact matches
  for (const pref of preferences) {
    if (normalizedAvailable.includes(pref.language)) {
      return availableLanguages[normalizedAvailable.indexOf(pref.language)];
    }
  }

  // Second pass: language prefix matches (e.g., 'es-MX' matches 'es')
  for (const pref of preferences) {
    const prefix = pref.language.split('-')[0];
    const prefixMatch = normalizedAvailable.find(lang => lang.startsWith(prefix));
    if (prefixMatch) {
      return availableLanguages[normalizedAvailable.indexOf(prefixMatch)];
    }
  }

  // Third pass: language suffix matches (e.g., 'es' matches 'es-MX')
  for (const pref of preferences) {
    const suffixMatch = normalizedAvailable.find(lang => lang.includes(pref.language));
    if (suffixMatch) {
      return availableLanguages[normalizedAvailable.indexOf(suffixMatch)];
    }
  }

  // Return first available language or default fallback
  return availableLanguages[0] || 'en';
}

/**
 * Get the current language from localStorage or detect from browser
 * @param availableLanguages - Array of available language codes
 * @returns Current language code
 */
export function getCurrentLanguage(availableLanguages: string[]): string {
  // Check localStorage first (user preference)
  const storedLanguage = localStorage.getItem('aiCentralLng');

  // Handle 'auto' language code
  if (storedLanguage === 'auto' && availableLanguages.includes('auto')) {
    return 'auto';
  }

  // If stored language exists and is valid (not 'auto'), use it
  if (storedLanguage && availableLanguages.includes(storedLanguage) && storedLanguage !== 'auto') {
    return storedLanguage;
  }

  // Default to 'auto' if no preference is stored or if stored preference is invalid
  return 'auto';
}

/**
 * Resolve the 'auto' language code to an actual language by detecting browser preferences
 * @param availableLanguages - Array of available language codes (excluding 'auto')
 * @returns Resolved language code
 */
export function resolveAutoLanguage(availableLanguages: string[]): string {
  // Filter out 'auto' from available languages for matching
  const actualLanguages = availableLanguages.filter(lang => lang !== 'auto');

  // Detect the best language match from browser preferences
  return findBestLanguageMatch(actualLanguages);
}

/**
 * Set the current language preference
 * @param language - Language code to set
 */
export function setCurrentLanguage(language: string): void {
  localStorage.setItem('aiCentralLng', language);
  // Note: i18next will handle the actual language change
}

/**
 * Get available languages with their display names
 * @returns Object mapping language codes to display names
 */
export function getAvailableLanguages(): Record<string, string> {
  return {
    'auto': 'Auto',
    'en': 'English',
    'es-MX': 'Español (México)'
  };
}

/**
 * Test function to verify auto language resolution works correctly
 * This can be used for debugging language detection issues
 */
export function testAutoLanguageResolution(): void {
  console.log('Testing auto language resolution...');

  // Test with 'auto' language
  const result = getLocalizedEnvVar('VITE_TEST_MESSAGE', 'auto', 'Default Message');
  console.log('Auto language resolution result:', result);

  // Test with specific languages
  const enResult = getLocalizedEnvVar('VITE_TEST_MESSAGE', 'en', 'Default Message');
  console.log('English language result:', enResult);

  const esResult = getLocalizedEnvVar('VITE_TEST_MESSAGE', 'es-MX', 'Default Message');
  console.log('Spanish language result:', esResult);
}

/**
 * Safely get environment variable value by key
 * @param key - The environment variable key
 * @returns The environment variable value or undefined
 */
function getEnvVar(key: string): string | undefined {
  // Use a safer approach to access environment variables
  // Avoid direct property access that might trigger Vite's define mechanism
  try {
    // Check if the key exists in import.meta.env without using bracket notation
    // that might cause issues with invalid identifiers
    const env = import.meta.env as Record<string, string | undefined>;
    return env[key];
  } catch {
    return undefined;
  }
}

/**
 * Get a language-aware environment variable
 * @param baseKey - The base environment variable key (e.g., 'VITE_MULTICHAT_WELCOME_MESSAGE')
 * @param currentLanguage - The current language code
 * @param fallback - Optional fallback value if env var doesn't exist
 * @returns The environment variable value for the current language or fallback
 */
export function getLocalizedEnvVar(
  baseKey: string,
  currentLanguage: string,
  fallback?: string
): string | undefined {
  // Handle 'auto' language by resolving it to the actual detected language
  const resolvedLanguage = currentLanguage === 'auto'
    ? resolveAutoLanguage(['en', 'es-MX']) // Use available languages for resolution
    : currentLanguage;

  // Convert language code to a format suitable for environment variable names
  // Replace hyphens with underscores to create valid identifiers
  const normalizedLanguage = resolvedLanguage.replace(/-/g, '_').toUpperCase();

  // Try language-specific variant first (e.g., VITE_MULTICHAT_WELCOME_MESSAGE_ES_MX)
  const languageSpecificKey = `${baseKey}_${normalizedLanguage}`;
  const languageSpecificValue = getEnvVar(languageSpecificKey);

  if (languageSpecificValue !== undefined) {
    return languageSpecificValue;
  }

  // Try base key as fallback (e.g., VITE_MULTICHAT_WELCOME_MESSAGE)
  const baseValue = getEnvVar(baseKey);

  if (baseValue !== undefined) {
    return baseValue;
  }

  // Return fallback if provided
  return fallback;
}

/**
 * Get a language-aware environment variable with boolean parsing
 * @param baseKey - The base environment variable key
 * @param currentLanguage - The current language code
 * @param fallback - Optional fallback boolean value
 * @returns The boolean environment variable value for the current language or fallback
 */
export function getLocalizedEnvVarBoolean(
  baseKey: string,
  currentLanguage: string,
  fallback?: boolean
): boolean {
  // Handle 'auto' language by resolving it to the actual detected language
  const resolvedLanguage = currentLanguage === 'auto'
    ? resolveAutoLanguage(['en', 'es-MX']) // Use available languages for resolution
    : currentLanguage;

  const value = getLocalizedEnvVar(baseKey, resolvedLanguage);

  if (value !== undefined) {
    return value.toLowerCase() === 'true';
  }

  return fallback ?? false;
}